import { useSyncExternalStore } from 'react';

/**
 * 全站共用的「前端已過期」旗標與「使用者有尚未儲存的輸入」旗標。
 * 放在 React 之外的 store，因為載入失敗發生在 lazy／翻譯載入器裡，沒有元件可以呼叫 setState。
 */
export interface AppVersionState {
    /** 伺服器上已經是新版本（或舊版 chunk 取不到），建議使用者重新整理 */
    isStale: boolean;
    /** 自上次換頁後，使用者在欄位裡輸入過內容 */
    hasUnsavedTyping: boolean;
}

const INITIAL_STATE: AppVersionState = { isStale: false, hasUnsavedTyping: false };

let currentState: AppVersionState = INITIAL_STATE;
const listeners = new Set<() => void>();

function updateState(changes: Partial<AppVersionState>): void {
    const next = { ...currentState, ...changes };
    if (
        next.isStale === currentState.isStale &&
        next.hasUnsavedTyping === currentState.hasUnsavedTyping
    ) {
        return;
    }
    currentState = next;
    listeners.forEach((notify) => notify());
}

export function markAppStale(): void {
    updateState({ isStale: true });
}

export function setUnsavedTyping(hasUnsavedTyping: boolean): void {
    updateState({ hasUnsavedTyping });
}

export function getAppVersionState(): AppVersionState {
    return currentState;
}

export function subscribeToAppVersion(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useAppVersionState(): AppVersionState {
    return useSyncExternalStore(subscribeToAppVersion, getAppVersionState, getAppVersionState);
}

/** 只給測試用：把旗標歸零 */
export function resetAppVersionState(): void {
    updateState(INITIAL_STATE);
}
